import React, { useState } from "react"
import { FileText, Share2 } from "lucide-react"
import { Button } from "src/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/components/ui/alert-dialog"
import { useToast } from "src/components/ui/toast"
import { ShareDocumentDrawer } from "src/features/documents/components/ShareDocumentDrawer"
import { useSendDemandLetter } from "../hooks/useCollectionMutations"

export default function DemandLetterButton({ bookingId, installmentId, customerName }) {
  const [open, setOpen] = useState(false)
  const [generatedDoc, setGeneratedDoc] = useState(null)
  const [shareOpen, setShareOpen] = useState(false)
  const { mutate, isPending } = useSendDemandLetter()
  const { toast } = useToast()

  async function handleConfirm() {
    try {
      const result = await mutate(bookingId, installmentId)
      setGeneratedDoc(result?.document || null)
      toast({
        title: "Demand letter generated",
        description: `Demand letter for ${customerName} is ready.`,
      })
      setOpen(false)
      if (result?.view_url || result?.download_url) {
        window.open(result.view_url || result.download_url, "_blank")
      }
    } catch (errors) {
      toast({ variant: "destructive", title: "Error", description: errors?.[0] || "Failed to generate demand letter" })
    }
  }

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-[12px]">
            <FileText className="h-3.5 w-3.5" />
            Demand
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generate demand letter?</AlertDialogTitle>
            <AlertDialogDescription>
              This will generate a formal payment demand letter for {customerName}. The PDF will be saved to the booking's documents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={isPending}>
              {isPending ? "Generating…" : "Generate Letter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {generatedDoc && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-[12px]"
          onClick={() => setShareOpen(true)}
          title="Share demand letter"
        >
          <Share2 className="h-3.5 w-3.5" />
          Share
        </Button>
      )}
      <ShareDocumentDrawer document={generatedDoc} open={shareOpen} onOpenChange={setShareOpen} />
    </>
  )
}
